import { format, isValid, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { Filters } from './FiltersContext';

import type { IEvent } from '..';

type FilterKey = keyof Filters;
const paramKeys: Record<FilterKey, string> = {
	selectedEventTypes: 'eventTypes',
	selectedEventTags: 'eventTags',
	selectedEventLocations: 'locations',
	selectedCountries: 'countries',
	selectedStates: 'states',
	selectedCities: 'cities',
};

export const parseQueryParams = (instanceId?: string): { searchTerm: string; filters: Filters } => {
	const params = new URLSearchParams(window.location.search);

	// Create instance-specific parameter prefix
	const prefix = instanceId ? `${instanceId}-` : '';

	const searchTerm = params.get(`${prefix}search`) ?? '';

	const filters = (Object.keys(paramKeys) as FilterKey[]).reduce<Filters>(
		(acc, key) => {
			const v = params.get(`${prefix}${paramKeys[key]}`);
			const arr = v && v.trim() ? v.split(',') : [];
			return {
				...acc,
				[key]: arr,
			};
		},
		// initial empty filters
		{
			selectedEventTypes: [],
			selectedEventTags: [],
			selectedEventLocations: [],
			selectedCountries: [],
			selectedStates: [],
			selectedCities: [],
		}
	);

	return { searchTerm, filters };
};

export const updateQueryParams = (searchTerm: string, filters: Filters, instanceId?: string) => {
	// Parse existing URL params to preserve other instances' parameters
	const params = new URLSearchParams(window.location.search);

	// Create instance-specific parameter prefix
	const prefix = instanceId ? `${instanceId}-` : '';

	// Remove old parameters for this instance
	params.delete(`${prefix}search`);
	(Object.values(paramKeys) as string[]).forEach((param) => {
		params.delete(`${prefix}${param}`);
	});

	// Add new parameters for this instance
	if (searchTerm.trim()) params.set(`${prefix}search`, searchTerm.trim());

	(Object.entries(paramKeys) as [FilterKey, string][]).forEach(([key, param]) => {
		const vals = filters[key];
		if (Array.isArray(vals) && vals.length) {
			params.set(`${prefix}${param}`, vals.filter((v) => v.trim()).join(','));
		}
	});

	const newSearch = params.toString();
	const base = window.location.pathname;
	window.history.pushState({}, '', newSearch ? `${base}?${newSearch}` : base);
};

export const getMonthKey = (date: Date): string => format(date, 'yyyy-MM');

export const toArray = (value) =>
	Array.isArray(value)
		? value
		: String(value || '')
				.split(',')
				.map((str) => str.trim())
				.filter(Boolean);

export const normalizeEvent = (event: IEvent): IEvent => {
	const actualStartStr = event.start.toString();
	const actualEndStr = event.end.toString();
	const { date: startDate, timezone } = parseEventDateWithTimezone(actualStartStr);
	const { date: endDate } = parseEventDateWithTimezone(actualEndStr);

	return {
		...event,
		id: event.UID,
		start: startDate,
		end: endDate,
		actualStart: new Date(actualStartStr),
		actualEnd: new Date(actualEndStr),
		eventType: toArray(event.eventType),
		tags: toArray(event.tags),
		address: event.address.trim(),
		timezone,
	};
};

/**
 * Extracts a timezone offset from an ISO datetime string and normalizes it
 * to ±HH:MM format.
 *
 * Supports offsets with and without a colon, as well as UTC (`Z`).
 * @example
 * getOffset('2025-06-01T10:00:00+05:30'); // '+05:30'
 *
 * @example
 * getOffset('2025-06-01T10:00:00+0530'); // '+05:30'
 */
export const getOffset = (s: string): string | null => {
	const m = s.match(/([+-])(\d{2}):?(\d{2})$|Z$/i);
	if (!m) return null;
	if (m[0] === 'Z' || m[0] === 'z') return '+00:00';
	return `${m[1]}${m[2]}:${m[3]}`;
};

/**
 * Parses an ISO datetime string and preserves the event's original wall-clock time
 * (the time in the event’s timezone, independent of the viewer’s
 * local timezone).
 *
 * Native APIs such as the `Date` constructor always normalize values to the local
 * system timezone. However, `react-big-calendar` expects `Date` objects that reflect
 * the event’s original timezone so events render correctly on the calendar grid.
 *
 * To achieve this, the function uses `utcToZonedTime`, which preserves
 * wall-clock time when converting dates.
 *
 * Since `utcToZonedTime` requires either a numeric offset or an IANA timezone name,
 * and we only receive ISO strings with numeric offsets, we extract the offset
 * manually before passing it to `utcToZonedTime`.
 *
 * @example
 * parseEventDateWithTimezone('2026-01-15T10:00:00-05:00').date?.getHours(); // 10
 *
 * @example
 * parseEventDateWithTimezone('2026-01-15T10:00:00').timezone; // ''
 *
 */
export const parseEventDateWithTimezone = (isoString?: string): { date: Date | null; timezone: string } => {
	const emptyResult = { date: null, timezone: 'GMT+00:00' };

	if (!isoString || typeof isoString !== 'string') {
		return emptyResult;
	}

	const dateString = isoString.trim();
	const parsedDate = parseISO(dateString);

	if (!dateString || !isValid(parsedDate)) {
		if (dateString) console.warn('Invalid date string:', isoString);
		return emptyResult;
	}

	const tzOffset = getOffset(dateString);

	if (!tzOffset) {
		return { date: parsedDate, timezone: '' };
	}

	const date = utcToZonedTime(dateString, tzOffset);
	return { date, timezone: `GMT${tzOffset}` };
};
